import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Users, UserPlus, Mail, Clock, X } from "lucide-react";

// Team management panel — visible only to the club owner when their tier is
// PREMIUM/TRIAL/admin. Editors and viewers do NOT see this UI.
export default function TeamPanel({ clubId, currentUserTier }) {
  const t = useT();
  const [members, setMembers] = useState(null);
  const [maxMembers, setMaxMembers] = useState(4);
  const [error, setError] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setError(null);
    api.get(`/clubs/${clubId}/members`)
      .then(({ data }) => {
        setMembers(data.members || []);
        setMaxMembers(data.max_members || 4);
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clubId]);

  const canInvite = ["PREMIUM", "TRIAL"].includes(currentUserTier);
  const atLimit = members !== null && members.length >= maxMembers;

  const removeMember = async (m) => {
    if (!window.confirm(t("Weet je zeker dat je {email} wilt verwijderen?").replace("{email}", m.email))) return;
    try {
      await api.delete(`/clubs/${clubId}/members/${m.id}`);
      toast.success(t("Teamlid verwijderd."));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeRole = async (m, newRole) => {
    if (m.role === newRole) return;
    try {
      await api.patch(`/clubs/${clubId}/members/${m.id}`, { role: newRole });
      toast.success(t("Rol bijgewerkt."));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitInvite = async (payload) => {
    setSaving(true);
    try {
      await api.post(`/clubs/${clubId}/members`, payload);
      toast.success(t("Uitnodiging verstuurd naar {email}").replace("{email}", payload.email));
      setShowInvite(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (members === null && !error) {
    return <div className="stat-label">{t("Laden…")}</div>;
  }

  return (
    <div className="border-2 border-foreground bg-card heavy-shadow" data-testid="team-panel">
      <div className="p-5 border-b-2 border-foreground flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="stat-label flex items-center gap-2">
            <Users className="w-4 h-4" /> {t("Team")}
          </div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">{t("Wie werkt er mee aan deze club")}</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {t("Nodig collega's uit als 'Viewer' (alleen bekijken) of 'Editor' (bekijken en bewerken, behalve verwijderen en teambeheer). Beschikbaar op PREMIUM.")}
          </p>
        </div>
        {canInvite && (
          <button
            type="button"
            onClick={() => setShowInvite(true)}
            disabled={atLimit}
            data-testid="team-invite-btn"
            className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <UserPlus className="w-4 h-4" /> {t("Teamlid uitnodigen")}
          </button>
        )}
      </div>

      {!canInvite && (
        <div className="p-4 bg-accent/10 border-b-2 border-foreground text-sm" data-testid="team-upgrade-notice">
          {t("Team-toegang zit in de PREMIUM-tier. Upgrade om collega's als viewer of editor toe te voegen.")}
        </div>
      )}

      {error && (
        <div className="p-3 bg-destructive/10 border-b-2 border-destructive text-destructive text-sm">{error}</div>
      )}

      {members !== null && members.length === 0 && (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="team-empty">
          {t("Nog geen teamleden. Klik op 'Teamlid uitnodigen' om te beginnen.")}
        </div>
      )}

      {members !== null && members.length > 0 && (
        <div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-foreground text-background">
                <th className="text-left px-4 py-2 font-display uppercase tracking-widest text-xs">{t("E-mail")}</th>
                <th className="text-left px-4 py-2 font-display uppercase tracking-widest text-xs">{t("Rol")}</th>
                <th className="text-left px-4 py-2 font-display uppercase tracking-widest text-xs">{t("Status")}</th>
                <th className="px-4 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-border" data-testid={`team-member-${m.email}`}>
                  <td className="px-4 py-2 font-mono-num">{m.email}</td>
                  <td className="px-4 py-2">
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      data-testid={`team-role-${m.email}`}
                      className="border-2 border-foreground bg-background px-2 py-1 text-xs font-display uppercase font-bold tracking-widest"
                    >
                      <option value="viewer">{t("Viewer")}</option>
                      <option value="editor">{t("Editor")}</option>
                    </select>
                  </td>
                  <td className="px-4 py-2">
                    {m.status === "active" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-positive">
                        ● {t("Actief")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        <Clock className="w-3 h-3" /> {t("In afwachting")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeMember(m)}
                      data-testid={`team-remove-${m.email}`}
                      className="p-1 border-2 border-destructive text-destructive hover:bg-destructive hover:text-background transition-colors"
                      title={t("Verwijder teamlid")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-3 border-t-2 border-foreground bg-muted/40 text-xs text-muted-foreground">
            {t("{n}/{max} teamleden gebruikt").replace("{n}", String(members.length)).replace("{max}", String(maxMembers))}
          </div>
        </div>
      )}

      {showInvite && (
        <InviteDialog t={t} onClose={() => setShowInvite(false)} onSubmit={submitInvite} saving={saving} />
      )}
    </div>
  );
}

function InviteDialog({ t, onClose, onSubmit, saving }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="team-invite-dialog-backdrop"
    >
      <div
        className="max-w-md w-full bg-card border-4 border-foreground p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        data-testid="team-invite-dialog"
      >
        <div className="stat-label flex items-center gap-2">
          <Mail className="w-4 h-4" /> {t("Nieuwe uitnodiging")}
        </div>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1 mb-4">
          {t("Nodig een teamlid uit")}
        </h2>
        <div className="space-y-3">
          <div>
            <label className="stat-label">{t("E-mailadres")}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              placeholder="teamlid@voorbeeld.nl"
              data-testid="team-invite-email"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 font-mono-num focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label className="stat-label">{t("Rol")}</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="team-invite-role"
              className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary font-display font-bold uppercase text-sm"
            >
              <option value="viewer">{t("Viewer — alleen bekijken")}</option>
              <option value="editor">{t("Editor — bekijken en bewerken")}</option>
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("We sturen een uitnodigingsmail met een link. Bestaande gebruikers hebben direct toegang; nieuwe gebruikers maken eerst een account.")}
          </p>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="team-invite-cancel"
            className="btn-brutal bg-background text-foreground border-2 border-foreground px-4 py-2 font-display uppercase font-bold text-sm"
          >
            {t("Annuleren")}
          </button>
          <button
            type="button"
            onClick={() => email.includes("@") && onSubmit({ email: email.trim(), role })}
            disabled={saving || !email.includes("@")}
            data-testid="team-invite-submit"
            className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? t("Versturen…") : t("Verstuur uitnodiging")}
          </button>
        </div>
      </div>
    </div>
  );
}
